import React from 'react';
import { dehydrate, QueryClient } from '@tanstack/react-query';
import { Skeleton } from 'antd';
import SkeletonAvatar from 'antd/es/skeleton/Avatar';
import { ArrowLeftIcon } from 'icons';
import { GetStaticProps } from 'next';
import Head from 'next/head';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useTranslations } from 'next-intl';

import { globalConst, isEmptyArr } from 'modules/global';
import {
  ContactSection,
  FilterSection,
  getUniversityByName,
  getUniversitySort,
  IAllUniversities,
  InfoTrigger,
  ItemRightSideFirstSection,
  ItemRightSideSecondSection,
  universitiesConst,
  universitySchemaJson,
  UniversityTypes,
  useUniversityBall,
  useUniversityByName,
  useUniversitySort
} from 'modules/universities';

import { EmptyData, NextHead, Visible } from 'components';

const Index = () => {
  const t = useTranslations();
  const { query, asPath } = useRouter();
  const { year, language, study } = query;
  const { university, isLoading } = useUniversityByName(query?.id);
  const { sortUniversity, isLoading: isSortLoading } = useUniversitySort(university?.id);

  const { universityBall, isLoading: isBallLoading } = useUniversityBall({
    univerId: university?.id,
    year: Number(year),
    language: language as string,
    study: study as string
  });

  return (
    <>
      <Head>
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(universitySchemaJson(university!, asPath))
          }}
        />
      </Head>
      <NextHead title={university?.name} description={university?.description} />

      <div className="mx-auto my-4 max-w-screen p-4">
        <Link href={globalConst.pageNames.UNIVERSITIES}>
          <div className="inline-flex cursor-pointer items-center justify-start gap-2 font-semibold">
            <ArrowLeftIcon /> {t('common.ortga_qaytish')}
          </div>
        </Link>
        <div className="block md:hidden">
          <InfoTrigger university={university} />
        </div>
        <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-4">
          {/* CHAP TOMON */}
          <div className="hidden space-y-4 self-start md:sticky md:top-4 md:block">
            <div className="rounded-xl bg-white p-3 shadow-sm">
              <Image
                width={225}
                height={150}
                className="max-h-40 min-h-40 w-full rounded-xl object-cover"
                src={university?.image?.replace(/\s+/g, '') || '/images/no-image.webp'}
                alt="no-image"
                title="Universitet rasmi mavjud emas"
              />
            </div>
            <div>
              {university?.type === UniversityTypes.PUBLIC &&
                (isSortLoading ? (
                  <div className="space-y-4 rounded-xl bg-white p-3 shadow-sm">
                    <Skeleton.Button active className="!h-3" />
                    {Array.from({ length: 3 }).map((_, index) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <div className="flex items-center gap-3" key={index}>
                        <Skeleton.Avatar active size={18} />
                        <Skeleton.Button active className="!w-30 !h-4" />
                      </div>
                    ))}

                    <Skeleton.Button active className="!h-3" />
                    {Array.from({ length: 3 }).map((_, index) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <div className="flex items-center gap-3" key={index}>
                        <Skeleton.Avatar active size={18} />
                        <Skeleton.Button active className="!w-30 !h-4" />
                      </div>
                    ))}
                  </div>
                ) : (
                  <FilterSection sortUniversity={sortUniversity} />
                ))}
            </div>
            <div className="rounded-xl bg-white p-3 shadow-sm">
              <ContactSection university={university} />
            </div>
          </div>

          {/* O'NG TOMON */}
          <div className="space-y-4 sm:col-span-3">
            {isLoading ? (
              <div className="flex space-x-4 rounded-xl bg-white p-4 px-6 shadow-sm ">
                <SkeletonAvatar size={80} active />
                <Skeleton active paragraph={{ rows: 2 }} />
              </div>
            ) : (
              <ItemRightSideFirstSection university={university} />
            )}

            <Visible
              if={isBallLoading && university?.id && year}
              else={
                <Visible
                  if={isEmptyArr(universityBall)}
                  else={
                    <ItemRightSideSecondSection
                      sortUniversity={sortUniversity}
                      universityBall={universityBall}
                    />
                  }
                >
                  <div className="rounded-xl bg-white p-3">
                    <h1 className="font-medium">{t('TalimYonalishlari')}</h1>

                    <EmptyData title={t('malumot_topilmadi')} />
                  </div>
                </Visible>
              }
            >
              <div className="rounded-xl bg-white p-3">
                <h1 className="font-medium">{t('TalimYonalishlari')}</h1>
                <div className="mt-5 space-y-3">
                  {Array.from({ length: 5 }).map((_, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <Skeleton.Button key={index} active className="!h-12 !w-full !rounded-xl" />
                  ))}
                </div>
              </div>
            </Visible>
          </div>
        </div>
      </div>
    </>
  );
};

export default Index;

export const getStaticProps: GetStaticProps = async ({ params, locale }) => {
  const queryClient = new QueryClient();

  const universityId = params?.id as string;

  await queryClient.prefetchQuery({
    queryKey: [universitiesConst.queryKeys.UNIVERSITY_BY_NAME, universityId],
    queryFn: getUniversityByName(universityId)
  });

  const university: IAllUniversities | undefined = queryClient.getQueryData([
    universitiesConst.queryKeys.UNIVERSITY_BY_NAME,
    universityId
  ]);

  await queryClient.prefetchQuery({
    queryKey: [universitiesConst.queryKeys.UNIVERSITIES_SORT, university?.id],
    queryFn: getUniversitySort(university?.id)
  });

  return {
    props: {
      dehydratedState: JSON.parse(JSON.stringify(dehydrate(queryClient))),
      messages: (await import(`../../../messages/${locale}.json`)).default
    },
    revalidate: 604800,
    notFound: !university
  };
};

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: 'blocking'
  };
}
